import Divider from '@shared/components/Divider/Divider';
import InputGroup from '@shared/components/InputGroup/InputGroup';
import { useTranslation } from 'react-i18next';

import { PermissionCategory, WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import { getPermissionCategories } from 'modules/CreateWorkspaceModal/utils';
import WorkspaceFormField from 'modules/CreateWorkspaceModal/WorkspaceCreateStep/WorkspaceFormField';
import { getDefaultRoleTranslation } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import PermissionCheckbox from 'shared/components/PermissionCheckbox/PermissionCheckbox';
import { permissionDescriptions } from 'shared/components/PermissionCheckbox/permissionDescriptions';
import PermissionColumnsList from 'shared/components/PermissionColumnsList/PermissionColumnsList';
import getEnabledPermissionsForRole from 'utils/permissions/getEnabledPermissionsForRole';
import { PermissionId } from 'utils/powerLevelsConfig';

const SHOW_ALL_PERMISSIONS = { workspace: Infinity, chat: Infinity, meeting: Infinity };

type PermissionEntry = PermissionCategory['permissions'][number];

interface RolePermissionsReadOnlyPanelProps {
  role: WorkspaceRoleV2;
  /** Inline settings view for actors without manage roles — hide permissions the role does not grant. */
  showOnlyEnabledPermissions?: boolean;
}

const RolePermissionsReadOnlyPanel = ({
  role,
  showOnlyEnabledPermissions = false,
}: RolePermissionsReadOnlyPanelProps) => {
  const { t } = useTranslation();

  const displayRoleName = getDefaultRoleTranslation(role.defaultRoleType, role.name) || role.name;
  const enabledIds = new Set(getEnabledPermissionsForRole(role).map((entry) => entry.id));
  const permissionCategories = getPermissionCategories({ actorPowerLevels: SHOW_ALL_PERMISSIONS });

  return (
    <>
      <InputGroup label={t('workspaces.role_name')}>
        <InputGroup.Input value={displayRoleName} readOnly disabled className="border-primary-50" />
      </InputGroup>
      <WorkspaceFormField label={t('workspaces.role_permissions')}>
        <div className="pb-2">
          {permissionCategories.map((category) => {
            const permissionsCount = category.permissions.length;
            const enabledCount = category.permissions.filter((entry) =>
              enabledIds.has(entry.id as PermissionId)
            ).length;
            const permissionsToShow = showOnlyEnabledPermissions
              ? category.permissions.filter((entry) => enabledIds.has(entry.id as PermissionId))
              : category.permissions;

            return (
              <div key={category.id} className="mb-4 bg-gray-10 rounded-lg">
                <div className="p-4">
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium text-size-sm">{t(category.labelKey)}</span>
                    <span className="text-sm">
                      (
                      {t('workspace_roles.permissions_included', {
                        enabled: enabledCount,
                        total: permissionsCount,
                      })}
                      )
                    </span>
                  </div>
                </div>
                <div className="px-3">
                  <Divider />
                </div>
                <div className="px-4 pb-4 pt-4">
                  {showOnlyEnabledPermissions && permissionsToShow.length === 0 ? (
                    <p className="text-size-sm font-light text-gray-60">
                      {t('workspace_roles.no_permissions_in_scope')}
                    </p>
                  ) : (
                    <PermissionColumnsList<PermissionEntry>
                      items={permissionsToShow}
                      getKey={(entry) => entry.id}
                      renderItem={(entry) => {
                        const permissionId = entry.id as PermissionId;

                        return (
                          <PermissionCheckbox
                            permission={{
                              id: permissionId,
                              label: t(entry.labelKey),
                              enabled: enabledIds.has(permissionId),
                            }}
                            size="sm"
                            disabled
                            description={permissionDescriptions[permissionId]}
                          />
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </WorkspaceFormField>
    </>
  );
};

export default RolePermissionsReadOnlyPanel;
